import {
    Inject,
    Injectable,
    Type,
} from '@angular/core';
import {
    DialogPosition,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { UiDialogComponent } from '@components/ui-dialog/ui-dialog.component';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import { IModalAdditionalSettings } from '@models';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface UiDialogRef<T, R> extends MatDialogRef<T, R> {
    customEvent: Observable<R>;
    afterClosedResult: () => Observable<R>;
}

const NOOP = () => { };

@Injectable({
    providedIn: 'root',
})
export class ModalService {
    constructor(
        @Inject(MatDialog)
        public dialog: MatDialog,
    ) { }

    // TODO: remove onOkay and onCancel to use only subscriptions
    public onConfirm(entryName: string | null, onOkay = NOOP, onCancel = NOOP, confirmationText?: string, focusNoOption = false) {
        const dialogRef = this.dialog.open<UiDialogComponent, never, boolean>(UiDialogComponent);
        dialogRef.componentInstance.modalText = confirmationText || `Are you sure you want to delete ${entryName}?`;
        dialogRef.componentInstance.focusNoButton = focusNoOption;

        const afterClosed$ = dialogRef.afterClosed().pipe(map(Boolean));
        afterClosed$.subscribe(result => {
            if (result) {
                onOkay();
            } else {
                onCancel();
            }
        });

        return afterClosed$;
    }

    public onAlert(alertText: string): Observable<any> {
        const dialogRef = this.dialog.open(UiDialogComponent);
        dialogRef.componentInstance.isAlertDialog = true;
        dialogRef.componentInstance.modalText = alertText;
        dialogRef.backdropClick().subscribe(() => {
            dialogRef.close();
        });

        return dialogRef.afterClosed();
    }

    public closeAllDialogs() {
        if (this.dialog) {
            this.dialog.closeAll();
        }
    }

    public getModalConfig(data?: any, additionalSettings?: IModalAdditionalSettings): any {
        return {
            data: data != null ? data : {},
            isNewEntry: data == null || !!(additionalSettings && additionalSettings.isDuplicate),
            isDuplicate: additionalSettings ? !!additionalSettings.isDuplicate : false,
            additionalData: additionalSettings ? additionalSettings.data : {},
            size: additionalSettings ? additionalSettings.size : '',
        };
    }

    public openModal<T, K>(arg: Type<UiModalComponent<T, K>>, data?: T | null, additionalSettings?: IModalAdditionalSettings) {
        let autoFocus = true;
        let position: DialogPosition = {
            top: '30px',
        };
        let disableClose = true;
        let hasBackdrop = true;
        let closeOnNavigation = false;
        const panelClass = ['ui-modal-dialog'];
        if (additionalSettings) {
            if (additionalSettings.size) {
                panelClass.push(`ui-modal-dialog--${additionalSettings.size}`);
            }
            if (additionalSettings.overflow) {
                panelClass.push(`ui-modal-dialog--overflow-visible`);
            }
            if (additionalSettings.additionalClasses != null) {
                panelClass.push(...additionalSettings.additionalClasses);
            }
            if (additionalSettings.autoFocus === false) {
                autoFocus = additionalSettings.autoFocus;
            }
            if (additionalSettings.position) {
                position = { ...position, ...additionalSettings.position };
            }
            closeOnNavigation = !!(additionalSettings.closeOnNavigation);
            disableClose = !additionalSettings.allowBackdropClose;
            hasBackdrop = !additionalSettings.disableBackdrop;
        }

        const dialogRef = this.dialog
            .open<UiModalComponent<T, K>>(
                arg, {
                    disableClose,
                    data: this.getModalConfig(data, additionalSettings),
                    position,
                    panelClass,
                    autoFocus,
                    hasBackdrop,
                    closeOnNavigation,
                }) as unknown as UiDialogRef<UiModalComponent<T, K>, K>;

        dialogRef.customEvent = dialogRef.componentInstance.customEvent;

        dialogRef.afterClosedResult = () => dialogRef.afterClosed().pipe(filterNullOrUndefined());

        return dialogRef;
    }
}
